import { Button, ContentItems, Flex, Text, TitledText } from '@/components';
import { ProjectResourceGuard } from '@/features/authentication/_components/ProjectResourceGuard';
import { PublishedProjectMutableGuard } from '@/features/authentication/_components/PublishedProjectMutableGuard';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import { getPinpointSceneAndFieldBySheetType } from '@/features/comment/utils/pinpoint';
import type { ProjectModel } from '@/features/project/projects/_models/project';
import { useId } from '@/hooks';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';
import { ProjectOtherNotesFormDrawer } from '../../project-information/_components/ProjectOtherNotesFormDrawer';

type Project = z.infer<typeof ProjectModel>;

type Props = Pick<
  Project,
  'increaseDecreaseMainReason' | 'otherSpecialNotes'
> & {
  editable: Project['isLocked'];
  isSkippedReview: boolean;
};

export const ProjectOtherNotesInformation: FC<Props> = ({
  increaseDecreaseMainReason,
  otherSpecialNotes,
  editable,
  isSkippedReview,
}) => {
  const { t } = useTranslation();
  const { id } = useId();
  const { setDrawerState } = useProjectDetailDrawerState();

  return (
    <>
      <Flex vertical gap={16}>
        <Flex justify="space-between">
          <Text
            text={t('increase-decrease-main-reason-and-other-special-notes')}
            variant="h3"
          />
          <ProjectResourceGuard onlyEdit>
            <PublishedProjectMutableGuard>
              {editable && (
                <Button
                  type="primary"
                  onClick={() => {
                    setDrawerState({
                      open: true,
                      type: 'other-notes',
                    });
                  }}
                >
                  {t('edit')}
                </Button>
              )}
            </PublishedProjectMutableGuard>
          </ProjectResourceGuard>
        </Flex>

        <ContentItems
          items={[
            <PinpointMarker
              key="increase-decrease-main-reason"
              pinpoint={getPinpointSceneAndFieldBySheetType({
                pinpointSheetType: isSkippedReview ? 'RS_SKIPPED_REVIEW' : 'RS',
                rs: {
                  path: { project: id },
                  field: 'rs.increase-decrease-main-reason',
                },
                rsSkippedReview: {
                  path: { project: id },
                  field: 'rs.skippedReview.increase-decrease-main-reason',
                },
              })}
            >
              <TitledText
                title={t('increase-decrease-main-reason')}
                text={increaseDecreaseMainReason}
              />
            </PinpointMarker>,
            <PinpointMarker
              key="other-special-notes"
              pinpoint={getPinpointSceneAndFieldBySheetType({
                pinpointSheetType: isSkippedReview ? 'RS_SKIPPED_REVIEW' : 'RS',
                rs: {
                  path: { project: id },
                  field: 'rs.other-special-notes',
                },
                rsSkippedReview: {
                  path: { project: id },
                  field: 'rs.skippedReview.other-special-notes',
                },
              })}
            >
              <TitledText
                title={t('other-special-notes')}
                text={otherSpecialNotes}
              />
            </PinpointMarker>,
          ]}
        />
      </Flex>
      <ProjectResourceGuard onlyEdit>
        <ProjectOtherNotesFormDrawer />
      </ProjectResourceGuard>
    </>
  );
};
